import { JAVA_SERVICE_ENDPOINTS } from '@buildwise/libs/services';
import CompanyAPI from 'api/CompanyAPI';
import { useQuery } from 'react-query';
import { companyAdminKeys } from './query-keys';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { queryClient } from 'config/queryClient';

export enum AccessRequestStatusTypes {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
}

export type AccessRequestType = {
  accessRequestMessage?: string;
  accessType: string;
  activityLog?: {
    date: Date | string;
    description: string;
    userName: string;
  }[];
  approvedBy?: string;
  createdAt: Date | string;
  createdBy: string;
  id: string;
  parentCompanyId?: string;
  requestedCompanyId: string;
  status: AccessRequestStatusTypes;
  updatedAt?: Date | string;
  updatedBy?: string;
  workspaceName: string;
};

function useAccessRequestsQuery(options: { shouldFetch?: boolean } = {}) {
  const { shouldFetch = true } = options;
  const { data: accessRequestsRes, ...rest } = useQuery<
    AxiosResponse<{
      accessRequestList: AccessRequestType[];
    }>,
    Error
  >({
    queryKey: companyAdminKeys.getAccessRequests({ ENTRY_TYPE: 'WORKSPACE' }),
    queryFn: () => CompanyAPI.getAccessRequests({ ENTRY_TYPE: 'WORKSPACE' }),
    refetchOnMount: 'always',
    staleTime: 60 * 1000,
    keepPreviousData: true,
    enabled: shouldFetch,
  });

  const accessRequests = useMemo(
    () =>
      accessRequestsRes?.data?.accessRequestList &&
      Array.isArray(accessRequestsRes?.data?.accessRequestList)
        ? accessRequestsRes?.data?.accessRequestList
        : [],
    [accessRequestsRes],
  );

  const updateCache = ({ request }: { request: AccessRequestType }) => {
    const queryStatus = queryClient.getQueryState(
      companyAdminKeys.getAccessRequests({ ENTRY_TYPE: 'WORKSPACE' }),
    );

    if (queryStatus?.status && queryStatus?.status !== 'idle') {
      queryClient.setQueryData<
        | AxiosResponse<
            {
              accessRequestList: AccessRequestType[];
            },
            Error
          >
        | undefined
      >(companyAdminKeys.getAccessRequests({ ENTRY_TYPE: 'WORKSPACE' }), (old) => {
        if (old) {
          const updatedRequests = [...(old.data.accessRequestList || [])];
          const index = updatedRequests.findIndex((req) => req.id === request.id);

          if (index > -1) {
            updatedRequests[index] = {
              ...updatedRequests[index],
              ...request,
            };
          } else {
            updatedRequests.push(request);
          }

          return {
            ...old,
            data: {
              ...old.data,
              accessRequestList: updatedRequests,
            },
          };
        }

        return old;
      });
    }
  };

  return {
    accessRequests,
    ...rest,
    updateCache,
  };
}

export default useAccessRequestsQuery;
